<!--
  BaseDialog — маленький диалог подтверждения, замена стандартного
  alert/confirm для коротких действий: удалить, отменить, подтвердить.

  Для больших окон с формами, настройками, сайдбаром и собственным footer
  используй BaseModal.
-->
<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="dialog-overlay"
      @click.self="handleOverlayClick"
    >
      <div
        class="card dialog-box"
        role="dialog"
        aria-modal="true"
        ref="dialogRef"
      >
        <h3 class="dialog-title" v-if="title">{{ title }}</h3>

        <div
          class="dialog-content"
          v-if="message.length > 0 || $slots.default"
        >
          <slot>
            <p class="dialog-message">{{ message }}</p>
          </slot>
        </div>

        <div class="dialog-actions">
          <component
            v-for="action in actions"
            :key="action.key"
            :is="getButtonComponent(action.type)"
            :class="[
              'dialog-btn',
              action.class,
              { 'delete-btn': action.type === 'danger' },
            ]"
            @click="handleAction(action.key)"
            :disabled="action.disabled"
          >
            {{ action.label }}
          </component>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { restoreModalFocus } from "@/utils/modalFocusUtils";

let nextDialogId = 1;
const activeDialogStack = [];

export default {
  name: "BaseDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    message: {
      type: String,
      default: "",
    },
    iconType: {
      type: String,
      default: "warning",
      validator: (value) =>
        ["warning", "info", "success"].includes(value),
    },
    actions: {
      type: Array,
      default: () => [
        {
          key: "confirm",
          label: "Подтвердить",
          type: "primary",
        },
        {
          key: "close",
          label: "Отмена",
          type: "secondary",
        },
      ],
    },
    closeOnOverlay: {
      type: Boolean,
      default: true,
    },
    closeOnEscape: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["close", "action"],
  data() {
    return {
      previousBodyOverflow: "",
      isBodyLocked: false,
      handleKeydown: null,
      dialogId: nextDialogId++,
      focusReturnTarget: null,
    };
  },
  watch: {
    visible(val) {
      if (val) {
        this.openDialog();
      } else {
        this.closeDialog();
      }
    },
  },
  methods: {
    handleOverlayClick() {
      if (this.closeOnOverlay) {
        this.$emit("action", "close");
      }
    },

    handleAction(actionKey) {
      this.$emit("action", actionKey);
    },

    getButtonComponent(type) {
      switch (type) {
        case "primary":
          return "base-primary-button";
        case "danger":
        case "secondary":
        default:
          return "base-button";
      }
    },

    lockBodyScroll() {
      if (this.isBodyLocked) return;

      this.previousBodyOverflow = document.body.style.overflow;
      this.isBodyLocked = true;
      document.body.style.overflow = "hidden";
    },

    unlockBodyScroll() {
      if (!this.isBodyLocked) return;

      this.isBodyLocked = false;
      document.body.style.overflow = this.previousBodyOverflow;
      this.previousBodyOverflow = "";
    },

    removeKeydownListener() {
      if (!this.handleKeydown) return;

      document.removeEventListener("keydown", this.handleKeydown);
      this.handleKeydown = null;
    },

    openDialog() {
      if (!activeDialogStack.includes(this.dialogId)) {
        this.focusReturnTarget = document.activeElement;
        activeDialogStack.push(this.dialogId);
      }
      this.lockBodyScroll();
      this.$nextTick(() => this.trapFocus());
    },

    closeDialog() {
      const dialog = this.$refs.dialogRef;
      const wasTopDialog = this.isTopDialog();
      const focusReturnTarget = this.focusReturnTarget;
      this.focusReturnTarget = null;
      const index = activeDialogStack.indexOf(this.dialogId);
      if (index !== -1) activeDialogStack.splice(index, 1);
      this.unlockBodyScroll();
      this.removeKeydownListener();
      restoreModalFocus(focusReturnTarget, dialog, wasTopDialog);
    },

    isTopDialog() {
      return activeDialogStack[activeDialogStack.length - 1] === this.dialogId;
    },

    trapFocus() {
      const dialog = this.$refs.dialogRef;
      if (!dialog) return;

      const focusable = dialog.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      first?.focus();
      this.removeKeydownListener();

      this.handleKeydown = (e) => {
        if (!this.isTopDialog()) return;

        if (e.key === "Escape") {
          if (this.closeOnEscape) {
            this.$emit("action", "close");
          }
          return;
        }

        if (e.key !== "Tab") return;

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last?.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first?.focus();
          }
        }
      };

      document.addEventListener("keydown", this.handleKeydown);
    },
  },
  mounted() {
    if (this.visible) this.openDialog();
  },
  beforeUnmount() {
    this.closeDialog();
  },
};
</script>

<style scoped>
.dialog-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2002;
  backdrop-filter: blur(2px);
  padding: 16px;
}

.dialog-box {
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
}

.dialog-title {
  font-size: 18px;
  line-height: 1.3;
  word-break: break-word;
}

.dialog-content {
  line-height: 1.5;
}

.dialog-message {
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
}

.dialog-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

:deep(.delete-btn) {
  background-color: #fff !important;
  border: 1px solid var(--color-danger) !important;
  color: var(--color-danger) !important;
}

:deep(.delete-btn):hover {
  background-color: var(--color-danger) !important;
  color: #fff !important;
}

@media (max-width: 480px) {
  .dialog-box {
    padding: 20px;
  }

  .dialog-actions {
    flex-direction: column-reverse;
  }

  .dialog-actions :deep(button) {
    width: 100%;
  }
}
</style>
